import { VideoResolution } from '@/ai/video-models';
import { getTrendCredits, TRENDS } from '@/config/trends';
import { Routes } from '@/lib/routes';

export interface DwpTextLink {
  label: string;
  href: string;
}

export interface DwpCopy {
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  breadcrumbHome: string;
  breadcrumbCurrent: string;
  title: string;
  /** Trailing words of `title` shown in the green accent colour. */
  titleAccent: string;
  subtitle: string;
  quickAnswer: string;
  storyboard: { label: string; captions: [string, string, string] };
  quickFacts: { label: string; value: string }[];
  comparison: {
    title: string;
    intro: string;
    columns: string[];
    rows: string[][];
  };
  pain: { title: string; paragraphs: string[] };
  features: {
    title: string;
    intro: string;
    items: { title: string; body: string }[];
  };
  advantage: {
    title: string;
    items: { title: string; body: string }[];
  };
  input: { title: string; intro: string; items: string[] };
  steps: {
    title: string;
    intro: string;
    items: { name: string; text: string }[];
  };
  useCases: { title: string; columns: string[]; rows: string[][] };
  decision: {
    title: string;
    columns: string[];
    rows: { goal: string; tool: DwpTextLink | string; why: string }[];
  };
  fileHandling: { title: string; items: string[]; pricingLink: DwpTextLink };
  pricing: { title: string; intro: string };
  faqTitle: string;
  faqs: { question: string; answer: string }[];
  cta: { title: string; body: string; button: string };
  disclaimer: string;
  lastUpdatedLabel: string;
}

const TREND = TRENDS['devil-wears-prada'];
const SECONDS = TREND.templateSeconds;
const SD_CREDITS = getTrendCredits(TREND, VideoResolution.P480);
const HD_CREDITS = getTrendCredits(TREND, VideoResolution.P720);

/**
 * Copy for /devil-wears-prada-ai: the October 2026 "Prada Prada Prada"
 * TikTok transition, re-filmed from one selfie by the trend workbench
 * (`TRENDS['devil-wears-prada']`, a Seedance 2.5 edit of an original
 * 12-second template clip with a fictional performer). English only;
 * `/es/devil-wears-prada-ai` stays `noindex` (see `src/lib/locale.ts`).
 * Trend facts come from Metaratings (2026-10-10) and Habr (2026-10-15);
 * product facts from `src/config/trends.ts`, `docs/trends.md` and
 * `src/config/pricing-config.ts`.
 */
const DWP_COPY: { en: DwpCopy } = {
  en: {
    seoTitle: 'Devil Wears Prada AI: Prada Transition Video From One Selfie',
    seoDescription: `Make the Devil Wears Prada AI trend from one selfie: lip-sync at home, white panels shut, then a hard cut to a night street. ${SECONDS}s, shot for shot.`,
    keywords: [
      'devil wears prada ai',
      'devil wears prada ai trend',
      'devil wears prada trend',
      'prada prada prada trend',
      'devil wears prada ai video generator',
    ],
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Devil Wears Prada AI',
    title: 'Devil Wears Prada AI Video Generator',
    titleAccent: 'Video Generator',
    subtitle:
      'One selfie in. The Prada Prada Prada transition out: at-home lip-sync, white panels shut, hard cut to you on a night street.',
    quickAnswer: `This Devil Wears Prada AI generator re-films the October 2026 TikTok transition with the person in your selfie: an at-home lip-sync with finger horns, two white panels shut on the beat, then a hard cut to the same person on a night street in a black leather jacket. The motion, panels, and cut come from a ${SECONDS}-second template clip, so only the person changes. It does not make a clip from the film, and it is not the AI-style meme shown in The Devil Wears Prada 2.`,
    storyboard: {
      label:
        'Three-panel Devil Wears Prada AI storyboard sketch: lip-sync at home with finger horns, white panels pushed shut, then the same person on a night street in a black leather jacket',
      captions: [
        '1. At home: plain top, lamp light, lip-sync and finger horns.',
        '2. The beat: two white panels slide in and shut.',
        '3. Hard cut: night street, glass buildings, black leather jacket.',
      ],
    },
    quickFacts: [
      {
        label: 'Input',
        value:
          'One selfie of one person. JPEG, PNG, or WebP, up to 10MB. The jacket and the street come from the clip.',
      },
      {
        label: 'Output',
        value: `A ${SECONDS}-second MP4 in the 4:3 frame of the template clip, at 480p or 720p.`,
      },
      {
        label: 'Sound',
        value:
          'The video has no audio track. Add the trend sound in the app you post to.',
      },
      {
        label: 'Cost',
        value: `${SD_CREDITS} credits at 480p, ${HD_CREDITS} credits at 720p. Shown on the Generate button.`,
      },
    ],
    comparison: {
      title:
        'How is a Devil Wears Prada AI clip different from the CapCut template?',
      intro:
        'Most posts under the Devil Wears Prada trend name are CapCut template tutorials. A template needs your own footage of both halves; a Devil Wears Prada AI clip here needs one selfie.',
      columns: [
        'Question',
        'Devil Wears Prada AI here',
        'CapCut template',
        'Filming it yourself',
      ],
      rows: [
        [
          'What you provide',
          'One selfie of one person',
          'Your own video clips',
          'Two shoots: at home and at night',
        ],
        [
          'Night-street half',
          'Comes from the template clip',
          'You need a night clip',
          'You go out and film it',
        ],
        [
          'Leather jacket',
          'Worn in the template, no second outfit needed',
          'Only if you own one',
          'Only if you own one',
        ],
        [
          'Panels and cut',
          'Same timing as the template every run',
          'Fixed by the template',
          'Depends on your edit',
        ],
        [
          'Sound',
          'None; add it when you post',
          'Template sound',
          'Added in the app',
        ],
      ],
    },
    pain: {
      title: 'Why is the Prada transition hard to film yourself?',
      paragraphs: [
        'The Devil Wears Prada trend asks for two looks in two places: a plain top at home under a lamp, and a dressed-up version of you outside at night. That means changing clothes, going out after dark, and lining up the same framing twice.',
        'The cut also has to land on the beat right after the white panels close, or the joke falls flat. A Devil Wears Prada AI clip removes the second shoot: the template already has both halves, the panels, and the cut, and your selfie only sets who is in it.',
      ],
    },
    features: {
      title: 'Why make your Devil Wears Prada AI video here?',
      intro:
        'The workbench above is set up for this one transition, so a Devil Wears Prada AI video takes one upload and one click.',
      items: [
        {
          title: 'The whole transition, shot for shot',
          body: `The lip-sync, the finger horns, the closing white panels, the hard cut, and the night street come from a ${SECONDS}-second template clip, so every Devil Wears Prada AI run keeps the same timing.`,
        },
        {
          title: 'One selfie, both looks',
          body: 'Your face and hair go into both halves. The plain T-shirt and the black leather jacket come from the template, so no second photo or outfit is needed.',
        },
        {
          title: '480p or 720p',
          body: `Pick 480p for ${SD_CREDITS} credits or 720p HD for ${HD_CREDITS}. The length and the 4:3 frame always match the template clip.`,
        },
        {
          title: 'Clear credits and automatic refunds',
          body: 'The Generate button shows the credit cost before you confirm. If a generation fails or is blocked, its credits go back to your balance.',
        },
      ],
    },
    advantage: {
      title: 'Why does the clip still look like you?',
      items: [
        {
          title: 'Motion copied, not guessed',
          body: 'Because the transition comes from a real clip, the panels close at the same moment and the cut lands in the same place every time, instead of drifting from run to run.',
        },
        {
          title: 'The face stays yours',
          body: 'The Devil Wears Prada AI prompt tells the model to keep the face, hair, skin tone, and body shape from your photo in both halves, so the cut reads as the same person changing scenes.',
        },
      ],
    },
    input: {
      title: 'What photo works best for the Prada transition?',
      intro:
        'The model can only keep what it can see. A clear selfie gives the cleanest Devil Wears Prada AI result.',
      items: [
        'One person, face toward the camera, chest-up like a front-camera selfie.',
        'Soft, even light. Strong shadows, sunglasses, or heavy filters make the face less accurate.',
        'Hair visible: it carries into the windy night-street half after the Devil Wears Prada AI cut.',
        'JPEG, PNG, or WebP, up to 10MB.',
        'Only photos of yourself or of people who agreed to appear. No celebrities or strangers.',
      ],
    },
    steps: {
      title: 'How do I make a Devil Wears Prada AI video from one selfie?',
      intro:
        'Five steps from selfie to a Devil Wears Prada AI clip you can post.',
      items: [
        {
          name: 'Upload your selfie',
          text: 'Add one clear photo of one person in the Your selfie slot.',
        },
        {
          name: 'Pick a resolution',
          text: `480p costs ${SD_CREDITS} credits and 720p costs ${HD_CREDITS}. The length always matches the ${SECONDS}-second template.`,
        },
        {
          name: 'Generate',
          text: 'Press Generate. Signed out, you log in first and your photo stays in place.',
        },
        {
          name: 'Wait a few minutes',
          text: 'Most Devil Wears Prada AI videos are ready in 5 to 10 minutes, and they are saved to your history.',
        },
        {
          name: 'Download and add the sound',
          text: 'Download the MP4, pick the trend sound in TikTok or Reels when you post, and label it as AI-generated.',
        },
      ],
    },
    useCases: {
      title: 'Who makes the Prada Prada Prada video, and how?',
      columns: ['Who', 'Starting photo', 'Tip', 'Result'],
      rows: [
        [
          'Creators joining the trend',
          'A plain at-home selfie',
          'Face the camera, soft light',
          'The classic home-to-night-street cut with your face',
        ],
        [
          'Guys doing it as a joke',
          'A deadpan selfie',
          'Post it with the trend sound for the contrast',
          'The same transition played straight',
        ],
        [
          'Friends trading versions',
          'One selfie each',
          'Generate one clip per person',
          'A Devil Wears Prada AI series with the same cut',
        ],
      ],
    },
    decision: {
      title: 'Which tool fits your version of the trend?',
      columns: ['You want', 'Use', 'Why'],
      rows: [
        {
          goal: 'The trend from one selfie',
          tool: 'This page',
          why: 'This Devil Wears Prada AI workbench follows the template clip shot for shot.',
        },
        {
          goal: 'Your own outfit in the second half',
          tool: { label: 'Reference to Video', href: Routes.ReferenceToVideo },
          why: 'It takes several stills and a prompt, and invents new motion.',
        },
        {
          goal: 'A night-street scene with no photo',
          tool: { label: 'Text to Video', href: Routes.TextToVideo },
          why: 'Starts from a written scene instead of a person.',
        },
        {
          goal: 'A longer final clip',
          tool: { label: 'AI Video Extender', href: Routes.VideoExtend },
          why: 'It continues a completed clip with a few more seconds.',
        },
        {
          goal: 'A copy of a specific creator’s video',
          tool: 'Not supported',
          why: 'The template is an original clip made for this site, not a creator’s post.',
        },
      ],
    },
    fileHandling: {
      title: 'What happens to your photo and the finished clip?',
      items: [
        'You sign in to generate, so every Devil Wears Prada AI clip lands in your own account.',
        'Your photo is only used for the Devil Wears Prada AI video you ask for.',
        'Finished clips are saved to your history, where you can watch and download them again.',
        'Generating needs credits from a one-time credit pack. There is no subscription.',
      ],
      pricingLink: {
        label: 'See credit packs',
        href: Routes.Pricing,
      },
    },
    pricing: {
      title: 'Credit packs',
      intro: `Buy credits once. They never expire, and a failed generation is refunded. One ${SECONDS}-second Devil Wears Prada AI video is ${SD_CREDITS} credits at 480p.`,
    },
    faqTitle: 'Devil Wears Prada AI questions',
    faqs: [
      {
        question: 'Is this a scene from The Devil Wears Prada film?',
        answer:
          'No. The Devil Wears Prada AI trend is a TikTok transition named after a song lyric, and this tool re-films an original template clip with the person in your photo. It uses no film footage, stills, posters, logos, or actor likenesses, and it is not affiliated with 20th Century Studios or Prada.',
      },
      {
        question: 'What song is the Devil Wears Prada trend?',
        answer:
          'The trend runs on "andy warhol" by Kai Angel, released on 4 September 2026 on the album "Shh…"; fans named it after the Prada line in the chorus. Many clips use a mashup with Jakarta\u2019s "One Desire" that KANA DJ posted on 22 September 2026. This site does not add either track, and the video has no audio, so pick the sound in TikTok or Reels when you post.',
      },
      {
        question: 'Do I need a second photo for the night-street outfit?',
        answer:
          'No. One selfie is enough: the black leather jacket and the night street are part of the template clip. If you want your own outfit in the second half, use Reference to Video, which accepts several stills and a prompt.',
      },
      {
        question: 'Will my clip match the original trend?',
        answer: `It follows the ${SECONDS}-second template clip shot for shot: the same lip-sync, panels, and cut timing, with your face in both halves. The template is an original clip made for this site, not a copy of any creator\u2019s video, and faces and small details still vary a little from run to run.`,
      },
      {
        question: 'How much does a Devil Wears Prada AI video cost?',
        answer: `${SD_CREDITS} credits at 480p or ${HD_CREDITS} credits at 720p. Credits come from one-time packs, starting with 50 credits for $9.90, and they never expire. There are no free credits; if a video fails, its credits are refunded automatically.`,
      },
    ],
    cta: {
      title: 'Ready to make your Devil Wears Prada AI video?',
      body: 'Upload one selfie and the workbench above films you into the at-home to night-street cut.',
      button: 'Upload your selfie',
    },
    disclaimer:
      'The Devil Wears Prada is a 20th Century Studios film, and Prada is a trademark of its owner. This Devil Wears Prada AI page is an independent tool. It is not affiliated with, endorsed by, or sponsored by 20th Century Studios, the film\u2019s makers, Prada, or the artists behind the trend sound, and it uses no film footage, stills, posters, logos, or actor likenesses. The example clip shows a fictional AI-generated performer.',
    lastUpdatedLabel: 'Last updated',
  },
};

export function getDwpCopy(): DwpCopy {
  return DWP_COPY.en;
}
